import React from 'react';
import {
  AdornedInput,
  Alert,
  Box,
  Button,
  ButtonGroup,
  FormField,
  FormHelp,
  Heading,
  Input,
  Modal,
  Spinner,
} from '@zillow/constellation-10';
import { useCreateAccountContact } from '../../hooks/billingPortal';
import type { AdproductsBillingPortalAccountContactDto } from '@zg-rentals/ts-adproducts-client';

const emailRegex = /(.+)@(.+){2,}\.(.+){2,}/;

const DEFAULT_ERROR = 'Failed to add new contact, please try again later';

export interface AddContactModalProps {
  open: boolean;
  handleClose: () => void;
  handleSubmit: () => void;
  accountNumber: string;
  existingContacts: Array<AdproductsBillingPortalAccountContactDto>;
}

const AddContactModal = ({
  open,
  handleClose,
  handleSubmit,
  accountNumber,
  existingContacts,
}: AddContactModalProps) => {
  const [email, setEmail] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [validationMessage, setValidationMessage] = React.useState<string | null>(null);
  const isValidEmail = emailRegex.test(email);

  const { mutate, isPending } = useCreateAccountContact(accountNumber, email);

  const handleAddNewContact = () => {
    setErrorMessage(null);
    // @ts-expect-error - mutation types are not properly typed
    mutate(email, {
      onSuccess: handleSubmit,
      onError: (e) => {
        // @ts-expect-error - error structure is not typed
        if (e.error && e.error.message) {
          // @ts-expect-error - error message type is not typed
          setErrorMessage(e.error.message);
        } else {
          setErrorMessage(DEFAULT_ERROR);
        }
      },
    });
  };

  const existingEmails = React.useMemo(() => {
    const existing = new Set();
    existingContacts.forEach((contact) => {
      if (contact.workEmail) {
        existing.add(contact.workEmail);
      }
      if (contact.personalEmail) {
        existing.add(contact.personalEmail);
      }
    });
    return existing;
  }, [existingContacts]);

  const footer = (
    <ButtonGroup aria-label="Modal actions">
      <Button tone="neutral" emphasis="filled" onClick={handleClose}>
        Cancel
      </Button>
      <Button
        disabled={!isValidEmail || isPending || !!validationMessage}
        tone="brand"
        emphasis="filled"
        onClick={handleAddNewContact}
      >
        Confirm
      </Button>
    </ButtonGroup>
  );

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleClose();
        }
      }}
      size="sm"
      header={
        <Heading level={5} css={{ textAlign: 'center' }}>
          Add a new contact
        </Heading>
      }
      body={
        <>
          <FormField
            control={
              <AdornedInput
                input={
                  <Input
                    disabled={isPending}
                    value={email}
                    placeholder="example@example.com"
                    error={!!validationMessage}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setEmail(e.target.value);
                      if (existingEmails.has(e.target.value)) {
                        setValidationMessage('Email already exists');
                      }
                    }}
                  />
                }
                endAdornment={
                  isPending ? (
                    <AdornedInput.Adornment>
                      <Spinner />
                    </AdornedInput.Adornment>
                  ) : undefined
                }
              />
            }
            description={validationMessage ? <FormHelp error={true}>{validationMessage}</FormHelp> : null}
          />
          {errorMessage && (
            <Box css={{ paddingTop: 'md' }}>
              <Alert tone="critical">{errorMessage}</Alert>
            </Box>
          )}
        </>
      }
      footer={footer}
      closeButton={null}
    />
  );
};

export default AddContactModal;
